import { DEV } from 'cc/env';
import { Canvas, director, game, Node, toDegree, v2, v3, Vec3, warn, _decorator } from 'cc';
import Delay from '../../Utils/Delay';
import { easingEnumToFunction, EasingType } from '../../Utils/EasingType';
import BaseMovingEffect, { BaseMovingEffectOption } from './BaseMovingEffect';
import PathData, { SPEED_CURVE_LENGTH } from './Bezier/PathData';
import { PathDataType, SEGEMENT_AVERAGE_DURATION, SegmentAverageDurationType, SpeedCurveDataType } from './Bezier/PathDataType';
import RunPath from './Bezier/RunPath';
import OrientationManager from '../../../OrientationManager';
import BaseGame from '../../../Game';

const { ccclass, property } = _decorator;

// const FRAME_RATE = 30;

export type BezierMovingEffectOption = {
    pathName: string;
    pathData?: PathDataType;
    speedRate?: number;
    offset?: Vec3;
    reverse?: boolean;
    additionBaseNode?: Node;
    fixedRotate?: boolean;
    speedCurve?: EasingType | string | SpeedCurveDataType | SegmentAverageDurationType;
    smoothMotion?: boolean; // 未指定時使用元件設定
    maxTurnSpeed?: number; // 未指定時使用元件設定
} & BaseMovingEffectOption;

@ccclass('BezierMovingEffect')
export default class BezierMovingEffect extends BaseMovingEffect {
    public static readonly EVENT_TYPE = { ...BaseMovingEffect.EVENT_TYPE };

    private static _canvasNode: Node = null;

    @property({ displayName: DEV && '使用真實時間', tooltip: DEV && '啟用時, 時間將以實際時間計算,\n反之則以遊戲的passTime處理' })
    public realTimeMode: boolean = false;

    @property({
        displayName: DEV && '平滑移動',
        tooltip: DEV && '啟用時, 位置與角度在路徑點之間連續內插, 慢速游動不再一格一格前進\n關閉時與原本的表演完全相同',
    })
    public smoothMotion: boolean = false;

    @property({
        displayName: DEV && '最大轉向速度',
        tooltip: DEV && '每秒最多轉動的角度, 避免路徑尖角或折返處瞬間翻轉\n0 為不限制 (與原本的表演相同)',
        min: 0,
    })
    public maxTurnSpeed: number = 0;

    protected _startTime: number;
    protected _targetTime: number;
    protected _path: PathData;
    protected _easing: EasingType | string | SpeedCurveDataType | SegmentAverageDurationType;
    protected _additionRunSpeedCurve: RunPath;
    protected _isCustomEasingObject: boolean = false;
    protected _normalEndPromiseResolve: Function;

    protected _reverse: boolean = false;
    protected _fixedRotate: boolean = false;
    protected _speedRate: number = 1;
    protected _offset: Vec3;
    protected _overridePosition: boolean = false;
    protected _elapsedOnPause: number = 0;
    protected _baseNode: Node = null; // 用於計算基準座標, 預設為 this.node.parent
    protected _lastAngle: number = 0;
    protected _smoothMotion: boolean = false;
    protected _maxTurnSpeed: number = 0;
    protected _turnAngle: number = null; // 上一幀的角度 (限制轉向速度用)
    protected _deformationVersion: number = 0; // 計算路徑變形時, 路徑世界座標的版本 (RunPath.worldVersion)

    // 針對路徑變形的數值
    protected _actualStartPosition: Vec3 = v3();
    protected _actualEndPosition: Vec3 = v3();
    protected _deformationOffset: Vec3 = v3();
    protected _deformationXscale: number = 0;
    protected _deformationYscale: number = 0;

    public static get canvasNode() {
        if (!this._canvasNode?.isValid) {
            this._canvasNode = director.getScene().getComponentInChildren(Canvas).node;
        }
        return this._canvasNode;
    }

    public get sampleFrameCount() {
        return this._path?.runPath?.sampleCount;
    }

    /** @deprecated 會建立整條路徑的點陣列並常駐記憶體, 逐幀使用請改用 sampleFrameCount / getRunTimePosition */
    public get runTimePoints() {
        return this._path?.runPath?.runTimePoints;
    }

    public get currentTime() {
        return this.realTimeMode ? Date.now() : game.timePassed;
    }

    protected get currentTimeProgress() {
        const pastTime = Math.max(this.currentTime - this._startTime - this._elapsedOnPause, 0);
        const totalDuration = this._targetTime - this._startTime;
        const progress = pastTime / totalDuration;
        return progress;
    }

    protected get currentActualProgress() {
        const timeProgress = this.currentTimeProgress;
        const targetX = timeProgress * SPEED_CURVE_LENGTH;
        if (this._isCustomEasingObject) {
            const targetFrame = this._additionRunSpeedCurve.findIndexAtLeastX(targetX);
            if (targetFrame < 0) {
                return 1;
            }
            const targetProgress = this._additionRunSpeedCurve.getPoint(targetFrame).y / SPEED_CURVE_LENGTH;
            return targetProgress;
        }
        if (this._easing === SEGEMENT_AVERAGE_DURATION) {
            return this._path.getEasingProgress(timeProgress);
        }
        if (EasingType[this._easing as string] !== undefined) {
            return easingEnumToFunction(this._easing as number)(timeProgress);
        }
        if (Number.isNaN(+this._easing)) {
            const targetSpeed = this.game.bezierPath.getSpeed(this._easing as string);
            if (!targetSpeed) {
                return easingEnumToFunction(EasingType.linear)(timeProgress);
            }
            const targetFrame = targetSpeed.runPath.findIndexAtLeastX(targetX);
            if (targetFrame < 0) {
                return 1;
            }
            const targetProgress = targetSpeed.runPath.getPoint(targetFrame).y / SPEED_CURVE_LENGTH;
            return targetProgress;
        }
        if (this._easing === EasingType.linear) {
            return timeProgress;
        }
        return easingEnumToFunction(this._easing as number)(timeProgress);
    }

    // 計算當前播放的幀數
    protected get currentFrame(): number {
        const easingProgress = this.currentActualProgress;
        if (this._smoothMotion) {
            // 與下方相同的對應方式, 但保留小數 (整數時即為原本的幀)
            const exactFrame = Math.max(0, Math.min(this.sampleFrameCount - 1, this.sampleFrameCount * easingProgress));
            return this._reverse ? this.sampleFrameCount - 1 - exactFrame : exactFrame;
        }
        const frame = Math.max(0, Math.min(this.sampleFrameCount - 1, Math.trunc(this.sampleFrameCount * easingProgress)));
        if (this._reverse) {
            // 反路徑 => 反過來取陣列
            return this.sampleFrameCount - 1 - frame;
        }
        return frame;
    }

    public play(option: BezierMovingEffectOption) {
        return super.play(option);
    }

    // 將剩餘的路徑時間縮短, 使表演加速結束
    public speedUp(ratio: number) {
        const { currentTime } = this;
        const originTotalDuration = this._targetTime - this._startTime;
        const lastTime = this._targetTime - currentTime;
        const newTargetTime = currentTime + lastTime / ratio;
        const newStartTime = newTargetTime - originTotalDuration / ratio;
        this._startTime = newStartTime;
        this._targetTime = newTargetTime;
    }

    /** 變更目前這次表演的平滑移動 / 最大轉向速度 (之後的 play 仍依 option 或元件設定) */
    public setMotionSmoothing(smoothMotion: boolean, maxTurnSpeed: number = this._maxTurnSpeed) {
        this._smoothMotion = smoothMotion;
        this._maxTurnSpeed = maxTurnSpeed;
        this._turnAngle = null;
    }

    protected *run(option?: BezierMovingEffectOption) {
        this._path = this.game.bezierPath.getPath(option.pathName) ?? PathData.import(option.pathData);
        if (!this._path) {
            warn(`Path ${option.pathName} not found`);
            return;
        }
        const { fromPos, toPos, startAt, speedRate, offset, reverse, overrideDuration, additionBaseNode, speedCurve, fixedRotate, smoothMotion, maxTurnSpeed } =
            this._parseOption(option);
        this._smoothMotion = smoothMotion;
        this._maxTurnSpeed = maxTurnSpeed;
        this._turnAngle = null;
        const duration = overrideDuration ?? this._path.totalDuration;
        const { currentTime } = this;
        this._startTime = startAt;
        this._speedRate = speedRate;
        this._targetTime = startAt + duration / this._speedRate;
        if (currentTime > this._targetTime) {
            return;
        }
        this._offset = offset;
        this._baseNode = additionBaseNode;
        this._reverse = reverse;
        this._elapsedOnPause = 0;
        if (fixedRotate !== undefined) {
            this._fixedRotate = fixedRotate;
        } else {
            this._fixedRotate = this._path.config.fixedRotate;
        }
        if (speedCurve !== undefined) {
            this._easing = speedCurve;
        } else {
            this._easing = this._path.config.easing;
        }
        this._additionRunSpeedCurve = null;
        this._isCustomEasingObject = false;
        if (
            Number.isNaN(+this._easing) &&
            typeof this._easing !== 'number' &&
            this._easing !== SEGEMENT_AVERAGE_DURATION &&
            EasingType[this._easing as string] === undefined
        ) {
            this._isCustomEasingObject = true;
            if (typeof this._easing === 'string') {
                const curve = this.game.bezierPath.getSpeed(this._easing);
                this._additionRunSpeedCurve = curve.runPath;
            } else {
                const existSpeedCurve = this.game.bezierPath.getSpeed(this._easing.name);
                if (existSpeedCurve) {
                    this._additionRunSpeedCurve = existSpeedCurve.runPath;
                } else {
                    this._additionRunSpeedCurve = this.game.bezierPath.loadSingleSpeed(this._easing).runPath;
                }
            }
        }
        if (fromPos && toPos) {
            this._overridePosition = true;
            this._actualStartPosition = fromPos.clone();
            this._actualEndPosition = toPos.clone();
            this.rescalePosition();
        } else {
            this._overridePosition = false;
            this._actualStartPosition = v3();
            this._actualEndPosition = v3();
            this._deformationOffset = v3();
            this._deformationXscale = 1;
            this._deformationYscale = 1;
        }
        if (this._startTime > currentTime) {
            yield Delay.resolve(this._startTime - currentTime, this);
        }
        this.enabled = true;

        yield new Promise<void>((resolve) => {
            this._normalEndPromiseResolve = resolve;
        });
    }

    protected async finish() {
        this.enabled = false;
        this._normalEndPromiseResolve?.();
        this._normalEndPromiseResolve = null;
    }

    protected onLoad() {
        BaseGame.orientation.on(OrientationManager.EVENT_TYPE.RESIZE, this.rescalePosition, this);
    }

    protected onDestroy() {
        BaseGame.orientation?.off(OrientationManager.EVENT_TYPE.RESIZE, this.rescalePosition, this);
    }

    protected update(dt: number) {
        if (this._startTime === undefined) {
            if (this.sampleFrameCount) {
                this.updatePosition(1, 0);
            }
            this.stop();
            return;
        }
        if (this._targetTime < this.currentTime) {
            this.updatePosition(1, 0);
            this.stop();
            return;
        }
        if (this.isPaused || this.isStopping) {
            this._elapsedOnPause += dt * 1000;
            return;
        }
        const { currentTimeProgress, currentFrame } = this;
        this.updatePosition(currentTimeProgress, currentFrame);
        if (currentTimeProgress < 1) {
            this.limitTurnSpeed(dt);
        }
    }

    // 限制每幀的轉向角度 (maxTurnSpeed 為 0 時不處理)
    protected limitTurnSpeed(dt: number) {
        if (!(this._maxTurnSpeed > 0) || this._fixedRotate) {
            return;
        }
        if (this._turnAngle !== null) {
            const diff = ((((this.node.angle - this._turnAngle) % 360) + 540) % 360) - 180;
            const limit = this._maxTurnSpeed * dt;
            if (Math.abs(diff) > limit) {
                this.node.angle = this._turnAngle + Math.sign(diff) * limit;
            }
        }
        this._turnAngle = this.node.angle;
    }

    /**
     * 依據路徑資訊及指定時間更新位置, 角度, 動作
     * @param frame 指定幀
     */
    protected updatePosition(progress: number, frame: number) {
        // 檢測已游完路徑 => 回收
        if ((!this._reverse && progress >= 1) || (this._reverse && progress >= 1)) {
            this.node.position = this.getRunTimePosition(this._reverse ? 0 : this.sampleFrameCount - 1)
                .toLocal(this._baseNode)
                .add(this._offset);
            if (!this._fixedRotate) {
                if (this._overridePosition) {
                    const targetFrame = this._reverse ? frame + 1 : frame - 1;
                    if (targetFrame > 0 && targetFrame < this.sampleFrameCount) {
                        const diffVec = this.getRunTimePosition(frame).subtract(this.getRunTimePosition(targetFrame));
                        if (diffVec.x !== 0 || diffVec.y !== 0) {
                            this.node.angle = toDegree(v2(1, 0).signAngle(diffVec.toVec2()));
                        }
                    }
                } else {
                    this.node.angle = this.getRuntimeAngle(frame);
                }
            }
            this.stop();
            return;
        }
        this.node.position = this.getRunTimePosition(frame).toLocal(this._baseNode).add(this._offset);
        if (!this._fixedRotate) {
            if (this._overridePosition) {
                const targetFrame = this._reverse ? frame + 1 : frame - 1;
                if (targetFrame > 0 && targetFrame < this.sampleFrameCount) {
                    const diffVec = this.getRunTimePosition(frame).subtract(this.getRunTimePosition(targetFrame));
                    if (diffVec.x !== 0 || diffVec.y !== 0) {
                        this.node.angle = toDegree(v2(1, 0).signAngle(diffVec.toVec2()));
                    }
                }
            } else {
                this.node.angle = this.getRuntimeAngle(frame);
            }
        }
    }

    protected getRuntimeAngle(frame: number, reverse: boolean = this._reverse) {
        frame = Math.max(0, Math.min(this.sampleFrameCount - 1, frame));
        let lastFrame = reverse ? frame + 3 : frame - 3; // 預設取前後3格來計算角度
        lastFrame = Math.max(0, Math.min(this.sampleFrameCount - 1, lastFrame));
        if (lastFrame === frame) {
            if (lastFrame === 0 && reverse) {
                lastFrame += 5; // 避免座標太近 推斷角度時出現極端結果
            }
            if (frame === this.sampleFrameCount - 1 && !reverse) {
                lastFrame -= 5;
            }
        }
        lastFrame = Math.max(0, Math.min(this.sampleFrameCount - 1, lastFrame));
        frame = Math.max(0, Math.min(this.sampleFrameCount - 1, frame));

        const lastPosition = this.getRunTimePosition(lastFrame).toLocal(this._baseNode).add(this._offset);
        const curPosition = this.getRunTimePosition(frame).toLocal(this._baseNode).add(this._offset);

        const direction = curPosition.subtract(lastPosition);
        if (direction.x !== 0 || direction.y !== 0) {
            const angle = toDegree(v2(1, 0).signAngle(direction.toVec2()));
            this._lastAngle = angle;
            return angle;
        }
        return this._lastAngle;
    }

    protected getRunTimePosition(frame: number) {
        const { runPath } = this._path;
        // 路徑的世界座標在 resize 後更新了: 變形的基準也要用新的座標重算, 否則魚會偏移
        if (this._overridePosition && this._deformationVersion !== runPath.worldVersion) {
            this.rescalePosition();
        }
        const position = this._smoothMotion ? runPath.getPointAt(frame) : runPath.getPoint(frame);
        if (this._overridePosition) {
            position.subtract(this._deformationOffset);
            position.x *= this._deformationXscale;
            position.y *= this._deformationYscale;
            position.add(this._actualStartPosition);
        }
        return position;
    }

    protected rescalePosition() {
        if (!this._overridePosition) {
            return;
        }
        const { x: startX, y: startY } = this._actualStartPosition;
        const { x: endX, y: endY } = this._actualEndPosition;
        this._deformationVersion = this._path.runPath.worldVersion;
        const originStart = this._path.runPath.getPoint(0);
        const originEnd = this._path.runPath.getPoint(this.sampleFrameCount - 1);
        let xScale = (endX - startX) / (originEnd.x - originStart.x);
        let yScale = (endY - startY) / (originEnd.y - originStart.y);
        if (xScale === 0 || Number.isNaN(xScale)) {
            xScale = 1;
        }
        if (yScale === 0 || Number.isNaN(yScale)) {
            yScale = 1;
        }
        this._deformationOffset = originStart;
        this._deformationXscale = Number.isFinite(xScale) ? xScale : 1;
        this._deformationYscale = Number.isFinite(yScale) ? yScale : 1;
    }

    private _parseOption(option: BezierMovingEffectOption) {
        let { startAt } = option;
        if (startAt) {
            startAt = this.realTimeMode ? option.startAt : game.timePassed + Date.now() - option.startAt;
        }
        return {
            pathName: option.pathName,
            speedRate: option.speedRate ?? 1,
            overrideDuration: option.overrideDuration,
            target: option.target ?? this.node,
            fromPos: option.fromPos,
            toPos: option.toPos,
            offset: option.offset ?? v3(),
            startAt: startAt ?? this.currentTime,
            reverse: option.reverse !== undefined ? option.reverse : false,
            additionBaseNode: option.additionBaseNode ?? this.node.parent,
            speedCurve: option.speedCurve,
            fixedRotate: option.fixedRotate,
            smoothMotion: option.smoothMotion ?? this.smoothMotion,
            maxTurnSpeed: option.maxTurnSpeed ?? this.maxTurnSpeed,
        };
    }
}
